/**
 * Time-based categorization for scan results.
 * Simplified from the main app's MatchResults.tsx logic.
 */
import type { ScanResponse, ApplicationOpenCloseDates } from '@/models/Scans'

export interface ResultCategory {
  key: string
  label: string
  description?: string
  results: ScanResponse[]
  fullCount: number
}

// ── Date helpers ──────────────────────────────────────────────

function normaliseToLocal(date: string | Date | null, timeZone: string): Date | null {
  if (!date) return null
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Date(dateObj.toLocaleString('en-US', { timeZone }))
}

function isCurrent(dates: ApplicationOpenCloseDates[], localToday: Date, timeZone: string): boolean {
  if (!dates || dates.length === 0) return false
  return dates.some(({ application_open, application_close }) => {
    const open = application_open ? normaliseToLocal(application_open, timeZone) : localToday
    const close = normaliseToLocal(application_close, timeZone)
    return open && open <= localToday && (!close || close >= localToday)
  })
}

function isUpcomingWithin3mo(
  dates: ApplicationOpenCloseDates[],
  localToday: Date,
  cutoff3mo: Date,
  timeZone: string,
): boolean {
  if (!dates || dates.length === 0) return false
  return dates.some(({ application_open, application_close }) => {
    const open = application_open ? normaliseToLocal(application_open, timeZone) : null
    const close = normaliseToLocal(application_close, timeZone)
    return open && open > localToday && open <= cutoff3mo && (!close || close >= open)
  })
}

// ── Sort ──────────────────────────────────────────────────────

function sortResults(a: ScanResponse, b: ScanResponse): number {
  const rankDiff = Number(b.rank) - Number(a.rank)
  if (rankDiff !== 0) return rankDiff
  return String(a.id).localeCompare(String(b.id))
}

// ── Main categorization ──────────────────────────────────────

const MAX_PER_CATEGORY = 5

export function categorizeResults(results: ScanResponse[]): ResultCategory[] {
  if (!results || results.length === 0) return []

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const localToday = normaliseToLocal(today, timeZone)!

  const cutoff3mo = new Date(localToday)
  cutoff3mo.setMonth(localToday.getMonth() + 3)

  // All high-ranking open now (rank > 3, no has_questions filter — used for fullCount)
  const openNowAll = results
    .filter(
      r =>
        !r.opportunity.is_relational &&
        Number(r.rank) > 3 &&
        (isCurrent(r.application_open_close_dates, localToday, timeZone) ||
          r.opportunity.always_open),
    )
    .sort(sortResults)

  // Shown: only those with questions (required for application drafting), capped at 5
  const openNow = openNowAll
    .filter(r => r.opportunity.has_questions)
    .slice(0, MAX_PER_CATEGORY)

  const openNowAllIds = new Set(openNowAll.map(r => r.id))

  // All high-ranking upcoming (rank > 3, no has_questions filter — used for fullCount)
  const upcomingAll = results
    .filter(
      r =>
        !r.opportunity.is_relational &&
        Number(r.rank) > 3 &&
        !r.opportunity.always_open &&
        !openNowAllIds.has(r.id) &&
        isUpcomingWithin3mo(r.application_open_close_dates, localToday, cutoff3mo, timeZone) &&
        !isCurrent(r.application_open_close_dates, localToday, timeZone),
    )
    .sort(sortResults)

  // Shown: only those with questions, capped at 5
  const upcoming = upcomingAll
    .filter(r => r.opportunity.has_questions)
    .slice(0, MAX_PER_CATEGORY)

  // All high-ranking relational (rank > 3)
  const relationalAll = results
    .filter(r => r.opportunity.is_relational && Number(r.rank) > 3)
    .sort(sortResults)

  const relational = relationalAll.slice(0, MAX_PER_CATEGORY)

  const categories: ResultCategory[] = []

  if (openNowAll.length > 0) {
    categories.push({
      key: 'open-now',
      label: 'Open Now',
      results: openNow,
      fullCount: openNowAll.length,
    })
  }

  if (upcomingAll.length > 0) {
    categories.push({
      key: 'upcoming',
      label: 'Opening Soon',
      description: 'These opportunities open within the next 3 months.',
      results: upcoming,
      fullCount: upcomingAll.length,
    })
  }

  if (relationalAll.length > 0) {
    categories.push({
      key: 'relational',
      label: 'Relational Opportunities',
      description:
        'These funders work through direct relationships rather than formal applications. They are shown for reference but cannot be selected for a draft.',
      results: relational,
      fullCount: relationalAll.length,
    })
  }

  return categories
}
